/**
 * F44 (Wave 2D) — admin finance exports page.
 *
 * Route: `/finance/exports`. Permission: `financial:read` (view history);
 * generating an export additionally needs `financial:export` — the in-page
 * check `adminUser.permissions.includes('financial:export')` hides the Stepper
 * flow + leaves only the read-only history tab for non-export viewers.
 *
 * Generate flow = a Stepper (report+period+format+optional source/status
 * filters → confirm → result Banner with the 24h signed download link). The
 * filters mirror the settlements list query and ride the
 * GenerateFinanceExportInput.filters field. The history sub-tab is a Table over
 * GET /admin/financial/exports showing the audit trail (admin / report /
 * format / row count / file hash / created-at); clicking a history row re-GETs
 * a fresh 24h signed URL (failed rows have no file — no refresh control; a
 * null-URL refresh surfaces a "no file available" Toast).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  EmptyState,
  Pagination,
  Select,
  Skeleton,
  Stepper,
  Table,
  useToast,
  type TableColumn,
} from '@bravemove/ui-web';
import {
  FINANCE_EXPORT_FORMATS,
  FINANCE_EXPORT_REPORTS,
  FINANCE_REVENUE_SOURCES,
  FINANCE_STATEMENT_STATUSES,
  type FinanceExportFormat,
  type FinanceExportJob,
  type FinanceExportReport,
  type FinanceRevenueSource,
  type FinanceStatementStatus,
  type GenerateFinanceExportInput,
} from '@bravemove/types';
import { adminFinancialApi } from '../../api/financial';
import { useAuth } from '../../auth/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { defaultFinanceRange, formatShortDate } from '../../components/finance/financeHelpers';
import { FinanceNav } from './FinanceNav';

const PAGE_SIZE = 25;

type ExportTab = 'generate' | 'history';

export function FinanceExportsPage() {
  const { t, locale } = useI18n();
  const { adminUser } = useAuth();
  const canExport = adminUser?.permissions.includes('financial:export') ?? false;

  const [tab, setTab] = useState<ExportTab>(canExport ? 'generate' : 'history');

  return (
    <div className="space-y-6" data-testid="finance-exports-page">
      <PageHeader
        eyebrow="Finance"
        title={t('admin:financial.exports.title')}
        subtitle={t('admin:financial.exports.subtitle')}
      />
      <FinanceNav />

      <div role="tablist" className="flex flex-wrap items-center gap-2">
        {canExport ? (
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'generate'}
            onClick={() => setTab('generate')}
            className={tabClass(tab === 'generate')}
            data-testid="finance-exports-tab-generate"
          >
            {t('admin:financial.exports.tab.generate')}
          </button>
        ) : null}
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'history'}
          onClick={() => setTab('history')}
          className={tabClass(tab === 'history')}
          data-testid="finance-exports-tab-history"
        >
          {t('admin:financial.exports.tab.history')}
        </button>
      </div>

      {!canExport ? (
        <div data-testid="finance-exports-readonly-notice">
          <Banner tone="info">{t('admin:financial.exports.notExportPermitted')}</Banner>
        </div>
      ) : null}

      {tab === 'generate' && canExport ? <GenerateFlow /> : <HistoryTab locale={locale} />}
    </div>
  );
}

function tabClass(active: boolean): string {
  return [
    'inline-flex items-center rounded-full px-3 h-8 text-[12.5px] font-semibold transition-colors',
    active
      ? 'bg-accent text-accent-ink'
      : 'bg-surface-2 text-ink-2 border border-hairline-2 hover:bg-surface',
  ].join(' ');
}

function GenerateFlow() {
  const { t } = useI18n();
  const defaultRange = useMemo(() => defaultFinanceRange(), []);

  const [step, setStep] = useState(0);
  const [report, setReport] = useState<FinanceExportReport>('settlements');
  const [format, setFormat] = useState<FinanceExportFormat>('csv');
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  // Optional filters mirroring the settlements list query (exports-filters-not-
  // exposed fix). Source applies to summary/settlements/refunds; status only to
  // the settlements report (the backend ignores it elsewhere).
  const [filterSource, setFilterSource] = useState<FinanceRevenueSource | ''>('');
  const [filterStatus, setFilterStatus] = useState<FinanceStatementStatus | ''>('');

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<FinanceExportJob | null>(null);
  const [error, setError] = useState<string | null>(null);

  const steps = [
    { label: t('admin:financial.exports.step.report') },
    { label: t('admin:financial.exports.step.confirm') },
    { label: t('admin:financial.exports.step.result') },
  ];

  const showStatusFilter = report === 'settlements';

  const onGenerate = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const filters: NonNullable<GenerateFinanceExportInput['filters']> = {
        ...(filterSource ? { source: filterSource } : {}),
        ...(showStatusFilter && filterStatus ? { status: filterStatus } : {}),
      };
      const input: GenerateFinanceExportInput = {
        report,
        format,
        from,
        to,
        ...(Object.keys(filters).length > 0 ? { filters } : {}),
      };
      const job = await adminFinancialApi.generateExport(input);
      setResult(job);
      setStep(2);
    } catch {
      setError(t('admin:financial.exports.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const onReset = () => {
    setResult(null);
    setError(null);
    setStep(0);
  };

  return (
    <div className="space-y-6" data-testid="finance-exports-generate">
      <Stepper steps={steps} current={step} />

      {step === 0 ? (
        <div className="flex flex-col gap-4 rounded-xl border border-hairline bg-surface p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">
                {t('admin:financial.exports.field.report')}
              </label>
              <Select
                value={report}
                options={FINANCE_EXPORT_REPORTS.map((r) => ({
                  value: r,
                  label: t(`admin:financial.exports.report.${r}`),
                }))}
                onChange={(e) => setReport(e.target.value as FinanceExportReport)}
                data-testid="finance-exports-report"
                aria-label={t('admin:financial.exports.field.report')}
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">
                {t('admin:financial.exports.field.format')}
              </label>
              <Select
                value={format}
                options={FINANCE_EXPORT_FORMATS.map((f) => ({
                  value: f,
                  label: t(`admin:financial.exports.format.${f}`),
                }))}
                onChange={(e) => setFormat(e.target.value as FinanceExportFormat)}
                data-testid="finance-exports-format"
                aria-label={t('admin:financial.exports.field.format')}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">
                {t('admin:financial.common.from')}
              </label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                aria-label={t('admin:financial.common.from')}
                className="h-10 w-full rounded-[10px] border border-hairline-2 bg-surface px-3 text-[13.5px] text-ink"
                data-testid="finance-exports-from"
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">
                {t('admin:financial.common.to')}
              </label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                aria-label={t('admin:financial.common.to')}
                className="h-10 w-full rounded-[10px] border border-hairline-2 bg-surface px-3 text-[13.5px] text-ink"
                data-testid="finance-exports-to"
              />
            </div>
          </div>
          {/* Optional filters — mirror the settlements list query. */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">
                {t('admin:financial.settlements.filters.source')}
              </label>
              <Select
                value={filterSource}
                options={[
                  { value: '', label: t('admin:financial.settlements.filters.all') },
                  ...FINANCE_REVENUE_SOURCES.map((s) => ({
                    value: s,
                    label: t(`commission:source.${s}`, { defaultValue: s }),
                  })),
                ]}
                onChange={(e) => setFilterSource(e.target.value as FinanceRevenueSource | '')}
                data-testid="finance-exports-filter-source"
                aria-label={t('admin:financial.settlements.filters.source')}
              />
            </div>
            {showStatusFilter ? (
              <div>
                <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">
                  {t('admin:financial.settlements.filters.status')}
                </label>
                <Select
                  value={filterStatus}
                  options={[
                    { value: '', label: t('admin:financial.settlements.filters.all') },
                    ...FINANCE_STATEMENT_STATUSES.map((s) => ({
                      value: s,
                      label: t(`commission:status.${s}`, { defaultValue: s }),
                    })),
                  ]}
                  onChange={(e) => setFilterStatus(e.target.value as FinanceStatementStatus | '')}
                  data-testid="finance-exports-filter-status"
                  aria-label={t('admin:financial.settlements.filters.status')}
                />
              </div>
            ) : null}
          </div>
          <Banner tone="info">{t('admin:financial.exports.preTaxNote')}</Banner>
          <div className="flex justify-end">
            <Button onClick={() => setStep(1)} data-testid="finance-exports-next">
              {t('admin:financial.exports.next')}
            </Button>
          </div>
        </div>
      ) : null}

      {step === 1 ? (
        <div className="flex flex-col gap-4 rounded-xl border border-hairline bg-surface p-4">
          <dl className="grid grid-cols-2 gap-3 text-[13px]">
            <Detail
              label={t('admin:financial.exports.field.report')}
              value={t(`admin:financial.exports.report.${report}`)}
            />
            <Detail
              label={t('admin:financial.exports.field.format')}
              value={t(`admin:financial.exports.format.${format}`)}
            />
            <Detail label={t('admin:financial.common.from')} value={from} />
            <Detail label={t('admin:financial.common.to')} value={to} />
            {filterSource ? (
              <Detail
                label={t('admin:financial.settlements.filters.source')}
                value={t(`commission:source.${filterSource}`, { defaultValue: filterSource })}
              />
            ) : null}
            {showStatusFilter && filterStatus ? (
              <Detail
                label={t('admin:financial.settlements.filters.status')}
                value={t(`commission:status.${filterStatus}`, { defaultValue: filterStatus })}
              />
            ) : null}
          </dl>
          <Banner tone="info">{t('admin:financial.exports.preTaxNote')}</Banner>
          {error ? <Banner tone="danger">{error}</Banner> : null}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setStep(0)} disabled={submitting}>
              {t('admin:financial.exports.back')}
            </Button>
            <Button
              onClick={onGenerate}
              loading={submitting}
              data-testid="finance-exports-generate-confirm"
            >
              {submitting
                ? t('admin:financial.exports.generating')
                : t('admin:financial.exports.generate')}
            </Button>
          </div>
        </div>
      ) : null}

      {step === 2 && result ? (
        <div className="flex flex-col gap-4 rounded-xl border border-hairline bg-surface p-4">
          <div data-testid="finance-exports-result">
            <Banner tone="success">{t('admin:financial.exports.ready')}</Banner>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {result.downloadUrl ? (
              <a
                href={result.downloadUrl}
                className="inline-flex h-10 items-center rounded-[10px] bg-accent px-4 text-[13.5px] font-semibold text-accent-ink"
                data-testid="finance-exports-download"
              >
                {t('admin:financial.exports.download')}
              </a>
            ) : null}
            {result.fileHash ? (
              <span className="font-mono text-[11px] text-ink-3" data-testid="finance-exports-hash">
                {result.fileHash}
              </span>
            ) : null}
          </div>
          <Button variant="secondary" size="sm" onClick={onReset} data-testid="finance-exports-new">
            {t('admin:financial.exports.newExport')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-ink-3">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

function HistoryTab({ locale }: { locale: string }) {
  const { t } = useI18n();
  const { push } = useToast();
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<readonly FinanceExportJob[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [freshUrl, setFreshUrl] = useState<{ id: string; url: string } | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminFinancialApi.listExportJobs({ page, perPage: PAGE_SIZE });
      setRows(data.data);
      setTotal(data.pagination.total);
    } catch {
      setError(t('admin:financial.errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [page, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const onFreshLink = useCallback(
    async (id: string) => {
      try {
        const job = await adminFinancialApi.getExportJob(id);
        if (job.downloadUrl) {
          setFreshUrl({ id, url: job.downloadUrl });
        } else {
          // No file exists for this job (e.g. it failed) — say so instead of
          // silently doing nothing (the exports-filters-not-exposed fix, part 2).
          push({ tone: 'warning', title: t('admin:financial.exports.history.noFile') });
        }
      } catch {
        setError(t('admin:financial.errors.generic'));
      }
    },
    [t, push],
  );

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns = useMemo<readonly TableColumn<FinanceExportJob>[]>(
    () => [
      {
        key: 'createdAt',
        header: t('admin:financial.exports.history.column.createdAt'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink">{formatShortDate(row.createdAt, locale)}</span>
        ),
      },
      {
        key: 'admin',
        header: t('admin:financial.exports.history.column.admin'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2">{row.requestedByAdminDisplayName ?? '—'}</span>
        ),
      },
      {
        key: 'report',
        header: t('admin:financial.exports.history.column.report'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink">
            {t(`admin:financial.exports.report.${row.report}`)}
          </span>
        ),
      },
      {
        key: 'format',
        header: t('admin:financial.exports.history.column.format'),
        cell: (row) => (
          <span className="font-mono text-[11px] uppercase text-ink-2">{row.format}</span>
        ),
      },
      {
        key: 'rows',
        header: t('admin:financial.exports.history.column.rows'),
        align: 'end',
        cell: (row) => (
          <span className="font-mono text-[12.5px] tabular-nums text-ink-2">
            {row.rowCount ?? '—'}
          </span>
        ),
      },
      {
        key: 'hash',
        header: t('admin:financial.exports.history.column.hash'),
        cell: (row) => (
          <span
            className="font-mono text-[10px] text-ink-3"
            data-testid={`finance-export-hash-${row.id}`}
          >
            {row.fileHash ?? '—'}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('admin:financial.exports.history.column.status'),
        cell: (row) => (
          <span className="font-mono text-[11px] uppercase text-ink-2">{row.status}</span>
        ),
      },
      {
        key: 'download',
        header: t('admin:financial.exports.history.column.download'),
        align: 'end',
        cell: (row) =>
          // Failed jobs have no file — never offer a dead refresh control.
          row.status === 'failed' ? (
            <span
              className="text-[12px] text-ink-3"
              data-testid={`finance-export-nofile-${row.id}`}
            >
              —
            </span>
          ) : freshUrl?.id === row.id ? (
            <a
              href={freshUrl.url}
              className="text-[12px] font-semibold text-accent hover:underline"
              data-testid={`finance-export-download-${row.id}`}
            >
              {t('admin:financial.exports.download')}
            </a>
          ) : (
            <button
              type="button"
              className="text-[12px] font-semibold text-accent hover:underline"
              onClick={() => onFreshLink(row.id)}
              data-testid={`finance-export-fresh-${row.id}`}
            >
              {t('admin:financial.exports.history.refresh')}
            </button>
          ),
      },
    ],
    [t, locale, freshUrl, onFreshLink],
  );

  return (
    <div className="space-y-4" data-testid="finance-exports-history">
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title={t('admin:financial.exports.history.empty')} />
      ) : (
        <>
          <Table
            data={rows as FinanceExportJob[]}
            columns={columns as TableColumn<FinanceExportJob>[]}
            rowKey={(row) => row.id}
            ariaLabel={t('admin:financial.exports.history.title')}
          />
          {pageCount > 1 ? (
            <div className="flex justify-end">
              <Pagination page={page} pageCount={pageCount} onChange={setPage} />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default FinanceExportsPage;
